import React, { useEffect, useState, useRef } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, message, Typography } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserAddOutlined,
  PoweroffOutlined,
} from '@ant-design/icons';
import { config } from '../config';

interface Usuario {
  id: number;
  username: string;
  nombre: string;
  rol: string;
  estado: string;
  empresa: string;
  entidad: string;
}

const PanelControl: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<Usuario | null>(null);
  const [form] = Form.useForm();
  const passwordInputRef = useRef<any>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Estado local para forzar recargas
  const [refreshKey, setRefreshKey] = useState(0);

  // Recarga la tabla
  const refreshTable = () => {
    // Incrementar la clave para forzar recarga
    setRefreshKey(prevKey => prevKey + 1);
  };

  // Refresca la sección de usuarios centralizadamente (sin retraso artificial)
  const refreshUsuariosSection = () => {
    fetchUsuarios();
    refreshTable();
  };

  // Fetch usuarios
  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      // Usar ruta relativa para que funcione el proxy en desarrollo
      // En producción, el navegador resolverá la ruta correctamente
      const apiUrl = '/usuarios.php';
      console.log('Cargando usuarios desde:', apiUrl);

      const res = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(`Error HTTP ${res.status} al cargar usuarios:`, errorText);
        message.error(`Error HTTP ${res.status}: ${errorText}`);
        setUsuarios([]);
        return;
      }

      const data = await res.json();
      console.log('Datos de usuarios recibidos:', data);

      // Adaptamos el formato de los datos según lo que devuelva la API
      const usuariosData = data.usuarios || [];
      setUsuarios(usuariosData);
      console.log('Estado de usuarios actualizado:', usuariosData);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      message.error('Error al cargar usuarios (fetchUsuarios)');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  // Crear o editar usuario
  const handleOk = async () => {
    try {
      // Validar los campos del formulario
      const values = await form.validateFields();
      console.log('Form values:', values);

      // Verificar que tengamos un usuario para editar
      if (!editingUser || !editingUser.id) {
        message.error('Error: No hay usuario seleccionado para editar');
        return;
      }

      // Mostrar estado de carga
      setConfirmLoading(true);

      // Preparar datos para enviar - aseguramos que todos los campos estén incluidos
      const userData = {
        username: values.username,
        nombre: values.nombre,
        rol: values.rol,
        estado: values.estado,
        empresa: values.empresa || 'SEPROVINSA',
        entidad: values.entidad || 'SEPROVINSA',
      };

      console.log('Datos a enviar:', JSON.stringify(userData, null, 2));

      // Enviar petición al servidor
      const response = await fetch(
        `https://control.seprovinsa.com/api.php/usuarios/${editingUser.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(userData),
        }
      );

      // Obtener respuesta en texto para debugging
      const responseText = await response.text();
      console.log('Respuesta del servidor (texto):', responseText);

      try {
        // Intentar parsear la respuesta como JSON
        const responseData = JSON.parse(responseText);
        console.log('Respuesta del servidor (JSON):', responseData);

        if (responseData.success) {
          // Actualizar la lista de usuarios en el estado local (actualización optimista)
          setUsuarios(prevUsuarios =>
            prevUsuarios.map(user =>
              user.id === editingUser.id
                ? {
                    ...user,
                    username: userData.username,
                    nombre: userData.nombre,
                    rol: userData.rol,
                    estado: userData.estado,
                    empresa: userData.empresa,
                    entidad: userData.entidad,
                  }
                : user
            )
          );

          // Mostrar mensaje de éxito
          message.success('Usuario actualizado con éxito');

          // Cerrar el modal
          setModalVisible(false);

          // Opcional: recargar los datos desde el servidor para asegurar sincronización
          // Esto es redundante con la actualización optimista, pero garantiza
          // que los datos estén correctamente sincronizados con el servidor
          setTimeout(() => {
            fetchUsuarios();
          }, 1000);
        } else {
          // Mostrar mensaje de error del servidor
          message.error(responseData.message || 'Error al actualizar el usuario');
        }
      } catch (jsonError) {
        // Error al parsear la respuesta como JSON
        console.error('Error al parsear la respuesta JSON:', jsonError);
        message.error('Error en la respuesta del servidor');
      }
    } catch (validationError) {
      // Error de validación de formulario
      console.error('Error de validación del formulario:', validationError);
      message.error('Por favor, complete todos los campos requeridos correctamente');
    } finally {
      // Desactivar estado de carga
      setConfirmLoading(false);
    }
  };

  // Eliminar usuario
  const handleDelete = async (user: Usuario) => {
    Modal.confirm({
      title: `¿Eliminar permanentemente a ${user.username}?`,
      content:
        'Esta acción no se puede deshacer. El usuario será eliminado completamente del sistema.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        setLoading(true);
        // Optimista: eliminar del estado local
        setUsuarios(prevUsuarios => prevUsuarios.filter(u => u.id !== user.id));
        try {
          const url = `https://control.seprovinsa.com/api.php/usuarios/${user.id}`;
          const res = await fetch(url, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          });
          const data = await res.json();
          if (!data.success) {
            message.error(data.message || 'Error al eliminar usuario');
            refreshUsuariosSection();
          } else {
            refreshUsuariosSection();
          }
        } catch (err) {
          message.error('Error al eliminar usuario');
          refreshUsuariosSection();
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Desactivar usuario
  const handleDeactivate = async (user: Usuario) => {
    setLoading(true);
    // Optimista: actualizar estado local
    setUsuarios(prevUsuarios =>
      prevUsuarios.map(u => (u.id === user.id ? { ...u, estado: 'inactivo' } : u))
    );
    try {
      const url = `https://control.seprovinsa.com/api.php/usuarios/${user.id}/desactivar`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (!data.success) {
        message.error(data.message || 'Error al desactivar usuario');
        refreshUsuariosSection();
      } else {
        refreshUsuariosSection();
      }
    } catch (err) {
      refreshUsuariosSection();
    } finally {
      setLoading(false);
    }
  };

  // Reactivar usuario
  const handleActivate = async (user: Usuario) => {
    setLoading(true);
    // Optimista: actualizar estado local
    setUsuarios(prevUsuarios =>
      prevUsuarios.map(u => (u.id === user.id ? { ...u, estado: 'activo' } : u))
    );
    try {
      const url = `https://control.seprovinsa.com/api.php/usuarios/${user.id}/activar`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (!data.success) {
        message.error(data.message || 'Error al activar usuario');
        refreshUsuariosSection();
      } else {
        refreshUsuariosSection();
      }
    } catch (err) {
      refreshUsuariosSection();
    } finally {
      setLoading(false);
    }
  };

  // Cambiar contraseña manualmente
  const handleManualPasswordChange = (user: Usuario) => {
    let newPassword = '';
    Modal.confirm({
      title: `Cambiar contraseña para ${user.username}`,
      content: (
        <Input.Password
          ref={input => {
            passwordInputRef.current = input;
          }}
          placeholder="Nueva contraseña"
          onChange={e => {
            newPassword = e.target.value;
          }}
          autoFocus
        />
      ),
      onOk: async () => {
        if (!newPassword) {
          message.error('Debes ingresar una contraseña');
          return Promise.reject();
        }
        setLoading(true);
        try {
          console.log('Intentando cambiar contraseña para usuario:', user.id);
          console.log('Payload enviado:', { password: newPassword });

          const res = await fetch(
            `https://control.seprovinsa.com/api.php/usuarios/${user.id}/password`,
            {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
              body: JSON.stringify({ password: newPassword }),
            }
          );

          console.log('Respuesta status:', res.status);
          // Obtener el texto de la respuesta primero para inspeccionarlo
          const responseText = await res.text();
          console.log('Respuesta texto completo:', responseText);

          // Intentar parsear el texto como JSON, capturando errores
          let data;
          let success = false;
          try {
            data = JSON.parse(responseText);
            console.log('Respuesta data parseada correctamente:', data);
            success = data.success;
          } catch (parseError) {
            console.error('Error al parsear JSON:', parseError);
            // Si hay un error de parseo, buscar un posible JSON válido dentro de la respuesta
            const jsonMatch = responseText.match(/\{.*?\}/s);
            if (jsonMatch) {
              try {
                data = JSON.parse(jsonMatch[0]);
                console.log('JSON extraído de la respuesta:', data);
                success = data.success;
              } catch (extractError) {
                console.error('Error al extraer JSON de la respuesta:', extractError);
              }
            }

            // Si el status code es 200 y contiene ciertas palabras clave, considerarlo éxito
            if (
              res.status === 200 &&
              (responseText.includes('"success":true') ||
                responseText.includes('affected_rows') ||
                responseText.toLowerCase().includes('contraseña'))
            ) {
              console.log('Detectado éxito basado en el contenido de la respuesta');
              success = true;
            }
          }

          if (success) {
            message.success('Contraseña cambiada correctamente');
            refreshUsuariosSection();
          } else {
            const errorMsg = data?.message || 'Error al cambiar contraseña';
            message.error(errorMsg);
          }
        } catch (error) {
          console.error('Error completo:', error);
          message.error('Error al cambiar contraseña');
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => {
        passwordInputRef.current = null;
      },
    });
  };

  const columns = [
    { title: 'Usuario', dataIndex: 'username', key: 'username' },
    { title: 'Nombre', dataIndex: 'nombre', key: 'nombre' },
    {
      title: 'Rol',
      dataIndex: 'rol',
      key: 'rol',
      render: (rol: string) =>
        rol === 'admin' ? <Tag color="geekblue">Admin</Tag> : <Tag color="green">Usuario</Tag>,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado: string) =>
        estado === 'activo' ? <Tag color="green">Activo</Tag> : <Tag color="red">Inactivo</Tag>,
    },
    { title: 'Entidad', dataIndex: 'entidad', key: 'entidad' },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_: any, user: Usuario) => (
        <>
          <Button
            icon={<EditOutlined />}
            size="small"
            style={{ marginRight: 8 }}
            onClick={() => {
              console.log('Configurando usuario para editar:', user.username);
              console.log('DATOS COMPLETOS DEL USUARIO:', JSON.stringify(user, null, 2));

              // Guardar el usuario en el estado
              setEditingUser(user);

              // Mostrar modal (debe ser primero para que el form esté renderizado)
              setModalVisible(true);

              // Resetear el formulario para evitar valores anteriores
              form.resetFields();

              // Establecer valores después de un breve retardo para asegurar que el modal está renderizado
              setTimeout(() => {
                console.log('Estableciendo valores del formulario para:', user.username);
                console.log('Nombre a establecer:', user.nombre);

                try {
                  // Intentar diferentes métodos para asegurar que el campo "nombre" funcione
                  // Método 1: setFieldValue
                  form.setFieldValue('username', user.username);
                  form.setFieldValue('nombre', user.nombre);

                  // Método 2: setFieldsValue específico para nombre
                  form.setFieldsValue({
                    nombre: user.nombre,
                  });

                  // Método 3: establecer directamente el valor en el campo
                  const nombreInput = document.querySelector(
                    'input[id="nombre"]'
                  ) as HTMLInputElement;
                  if (nombreInput) {
                    nombreInput.value = user.nombre || '';
                  }

                  // Establecer el resto de campos normalmente
                  form.setFieldValue('rol', user.rol);
                  form.setFieldValue('estado', user.estado);
                  form.setFieldValue('empresa', user.empresa || 'SEPROVINSA');
                  form.setFieldValue('entidad', user.entidad || 'SEPROVINSA');

                  console.log(
                    'Valores establecidos con éxito. Campo nombre:',
                    form.getFieldValue('nombre')
                  );
                } catch (err) {
                  console.error('Error al establecer valores:', err);
                }
              }, 200);
            }}
          >
            Editar
          </Button>
          <Button
            icon={<ReloadOutlined />}
            size="small"
            style={{ marginRight: 8 }}
            onClick={() => handleManualPasswordChange(user)}
          >
            Cambiar Contraseña
          </Button>
          {user.estado === 'activo' ? (
            <Button
              icon={<PoweroffOutlined />}
              size="small"
              style={{
                marginRight: 8,
                background: '#faad14',
                borderColor: '#faad14',
                color: '#fff',
              }}
              onClick={() => handleDeactivate(user)}
            >
              Desconectar
            </Button>
          ) : (
            <Button icon={<PlusOutlined />} size="small" onClick={() => handleActivate(user)}>
              Activar
            </Button>
          )}
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(user)}>
            Eliminar
          </Button>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Typography.Title level={3} style={{ margin: 0, color: 'white' }}>
          Administrador
        </Typography.Title>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          style={{ marginBottom: 16 }}
          onClick={() => {
            setEditingUser(null);
            setModalVisible(true);
            form.resetFields();
          }}
        >
          Nuevo usuario
        </Button>
      </div>

      {/* Sección SEPROVINSA */}
      <div style={{ marginBottom: 40, borderRadius: 12, background: '#fff', padding: 24 }}>
        <h3
          style={{
            borderLeft: '4px solid #1890ff',
            paddingLeft: 10,
            marginBottom: 20,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          SEPROVINSA
        </h3>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={usuarios.filter(
            u =>
              u.empresa?.trim().toUpperCase() === 'SEPROVINSA' ||
              u.entidad?.trim().toUpperCase() === 'SEPROVINSA'
          )}
          loading={loading}
          pagination={{ pageSize: 8 }}
          key={`seprovinsa-${refreshKey}`}
        />
      </div>

      {/* Sección CLIENTES */}
      <div style={{ borderRadius: 12, background: '#fff', padding: 24 }}>
        <h3
          style={{
            borderLeft: '4px solid #52c41a',
            paddingLeft: 10,
            marginBottom: 20,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          CLIENTES
        </h3>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={usuarios.filter(
            u =>
              u.empresa?.trim().toUpperCase() !== 'SEPROVINSA' &&
              u.entidad?.trim().toUpperCase() !== 'SEPROVINSA'
          )}
          loading={loading}
          pagination={{ pageSize: 8 }}
          key={`clientes-${refreshKey}`}
        />
      </div>

      <Modal
        title={editingUser ? 'Editar usuario' : 'Nuevo usuario'}
        open={modalVisible}
        onOk={handleOk}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
        }}
        okText={editingUser ? 'Guardar cambios' : 'Crear usuario'}
        confirmLoading={confirmLoading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={
            editingUser
              ? {
                  username: editingUser.username,
                  nombre: editingUser.nombre,
                  rol: editingUser.rol,
                  estado: editingUser.estado,
                  empresa: editingUser.empresa || 'SEPROVINSA',
                  entidad: editingUser.entidad || 'SEPROVINSA',
                }
              : {
                  rol: 'usuario',
                  estado: 'activo',
                  empresa: 'SEPROVINSA',
                  entidad: 'SEPROVINSA',
                }
          }
        >
          <Form.Item
            name="username"
            label="Usuario"
            rules={[{ required: true, message: 'El usuario es obligatorio' }]}
          >
            {' '}
            <Input disabled={!!editingUser} />{' '}
          </Form.Item>
          <Form.Item
            name="nombre"
            label="Nombre completo"
            rules={[{ required: true, message: 'El nombre es obligatorio' }]}
            initialValue={editingUser?.nombre}
          >
            <Input
              placeholder="Nombre completo"
              defaultValue={editingUser?.nombre || ''}
              onChange={e => console.log('Valor actual de nombre:', e.target.value)}
            />
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label="Contraseña"
              rules={[{ required: true, message: 'La contraseña es obligatoria' }]}
            >
              {' '}
              <Input.Password />{' '}
            </Form.Item>
          )}
          <Form.Item name="rol" label="Rol" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="admin">Administrador</Select.Option>
              <Select.Option value="usuario">Usuario</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="estado" label="Estado" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="activo">Activo</Select.Option>
              <Select.Option value="inactivo">Inactivo</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="empresa"
            label="Empresa"
            rules={[{ required: true, message: 'La empresa es obligatoria' }]}
          >
            <Select>
              <Select.Option value="SEPROVINSA">SEPROVINSA</Select.Option>
              <Select.Option value="CLIENTE">CLIENTE</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="entidad"
            label="Entidad"
            rules={[{ required: true, message: 'La entidad es obligatoria' }]}
          >
            <Select>
              <Select.Option value="SEPROVINSA">SEPROVINSA</Select.Option>
              <Select.Option value="CLIENTE">CLIENTE</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PanelControl;
